import { ColorPaletteInterface } from '../colorPicker/pickers/paletteColorsPicker';
import { ColorPicker } from '../colorPicker';
import { Icon } from '../icon';
import { useDropdown } from '../hooks/useDropdown';
import React, { FC, useEffect, useState } from 'react';
import { HSLColor } from 'react-color';
import { hexToHSL } from 'src/shared/helpers';
import MdAdd from 'src/shared/material_icons/content/add.svg';
import MdClose from 'src/shared/material_icons/navigation/close.svg';
import AngleDown from 'src/shared/svg/angleDown';
import styled from 'styled-components';

const Container = styled.div<{ $isFormFieldSelection?: boolean; $isForTableUse?: boolean }>`
    display: grid;
    grid-gap: 20px;
    grid-template-columns: ${(props) => (props.$isFormFieldSelection ? '1fr' : props.$isForTableUse ? 'repeat(auto-fit, 40px)' : 'repeat(auto-fit, 80px)')};
    position: relative;
    padding-top: 0;
`;

const ColorBox = styled.div<{ $isFormFieldSelection?: boolean; $isForTableUse?: boolean }>`
    border: 1px solid #ddd;
    width: ${(props) => (props.$isFormFieldSelection ? '100%' : props.$isForTableUse ? '40px' : '80px')};
    height: ${(props) => (props.$isFormFieldSelection || props.$isForTableUse ? '40px' : '80px')};
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${(props) => props.theme.app.primary};
    cursor: pointer;
    position: relative;

    ${Icon} {
        width: 32px;
        height: 32px;
    }
`;

const ColorBoxFill = styled.div<{ color: string }>`
    background-color: ${(props) => props.color};
    width: 100%;
    height: 100%;
    border-radius: 5px;
`;

const ColorBoxClose = styled.div`
    position: absolute;
    top: -9px;
    right: -9px;
    border-radius: 10px;
    width: 18px;
    height: 18px;
    border: 1px #ddd solid;
    background-color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: ${(props) => props.theme.app.primary};

    &:hover {
        background-color: ${(props) => props.theme.app.secondary};
        color: white;
    }

    ${Icon} {
        position: absolute;
        right: -2px;
        width: 19px;
        height: 19px;
    }
`;

const ColorBoxArrow = styled.div<{ $isFormFieldSelection?: boolean; $isForTableUse?: boolean }>`
    position: absolute;
    bottom: 0px;
    right: 0px;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    background-color: white;
    border: 1px solid #ddd;
    border-radius: 0px 0px 5px 0px;
    padding: ${(props) => (props.$isFormFieldSelection || props.$isForTableUse ? '5px 7px' : '7px 9px')};

    ${Icon} {
        position: absolute;
        right: ${(props) => (props.$isFormFieldSelection || props.$isForTableUse ? '-5px' : '-1px')};
        top: ${(props) => (props.$isFormFieldSelection || props.$isForTableUse ? '0px' : '-2px')};
        width: ${(props) => (props.$isFormFieldSelection || props.$isForTableUse ? '4px' : '14px')};
        height: ${(props) => (props.$isFormFieldSelection || props.$isForTableUse ? '12px' : '20px')};
        opacity: 0.8;
    }

    svg {
        width: ${(props) => (props.$isFormFieldSelection || props.$isForTableUse ? '75%' : '100%')};
        height: ${(props) => (props.$isFormFieldSelection || props.$isForTableUse ? '100%' : '100%')};
    }
`;

const HexCodeContainer = styled.div`
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const HexCode = styled.p<{ $isFormFieldSelection?: boolean }>`
    margin: 0;
    ${(props) => props.$isFormFieldSelection && 'color: rgba(68, 90, 100, 1)'};
    ${(props) => props.$isFormFieldSelection && 'font-size: 13px'};
    cursor: pointer;
    line-height: 24px;

    &:hover {
        text-decoration: underline;
    }
`;

const HexInput = styled.input`
    width: 100%;
    height: 24px;
    font-size: 12px;
    padding: 2px 4px;
    margin: 0;
    border: 1px solid ${(props) => props.theme.app.primary_light};
    border-radius: 3px;
    font-family: ${(props) => props.theme.app.font};
    box-sizing: border-box;
    text-align: center;

    &:focus {
        outline: none;
        border-color: ${(props) => props.theme.app.secondary};
    }
`;

const ColorContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10;
`;

const EditableHexCode: FC<{
    color: string;
    isFormFieldSelection?: boolean;
    onChange: (color: string) => void;
}> = ({ color, isFormFieldSelection, onChange }) => {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(color);

    useEffect(() => {
        setValue(color);
    }, [color]);

    const hexRegex = /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/;

    const handleConfirm = () => {
        const trimmed = value.trim();
        const normalized = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
        if (hexRegex.test(normalized)) {
            onChange(normalized);
        } else {
            setValue(color);
        }
        setEditing(false);
    };

    return (
        <HexCodeContainer>
            {editing ? (
                <HexInput
                    autoFocus
                    value={value}
                    onChange={(e) => setValue(e.currentTarget.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            handleConfirm();
                        } else if (e.key === 'Escape') {
                            setValue(color);
                            setEditing(false);
                        }
                    }}
                    onBlur={handleConfirm}
                    onClick={(e) => e.stopPropagation()}
                />
            ) : (
                <HexCode
                    $isFormFieldSelection={isFormFieldSelection}
                    onClick={(e) => {
                        e.stopPropagation();
                        setEditing(true);
                    }}
                >
                    {color}
                </HexCode>
            )}
        </HexCodeContainer>
    );
};

const ColorSelection: FC<{
    colors: string[];
    availableColors?: ColorPaletteInterface[];
    menuPosition?: 'absolute' | 'fixed';
    isFormFieldSelection?: boolean;
    isForTableUse?: boolean;
    onClickDisabled?: boolean;
    showHexCode?: boolean;
    colorIndex?: number; // Optional index for the color in the palette
    colorNames?: string[]; // Optional names for the colors in the palette
    onChange: (index: number, color: string, name: string) => void;
    onAddClick?: () => void;
    onRemoveClick?: (index: number) => void;
}> = ({
    colors,
    availableColors,
    menuPosition = 'fixed',
    isFormFieldSelection = false,
    isForTableUse = false,
    onClickDisabled,
    showHexCode = true,
    colorIndex,
    colorNames,
    onChange,
    onAddClick,
    onRemoveClick
}) => {
    const [currentColor, setCurrentColor] = useState<HSLColor | null>(null);
    const [color, setColor] = useState<string | null>(null);
    const [setOpen, setClose, isOpen, Dropdown] = useDropdown();

    const [currentIndex, setCurrentIndex] = useState<number>(0);

    // NB: no window 'click'/'scroll' listener clearing `color` here — `useDropdown`
    // already handles outside-click close (on mousedown) and scroll repositioning.
    // The old listener fired on the SAME click that opened the picker, nulling
    // `color` before `{color && isOpen}` could render it → picker never opened.

    useEffect(() => {
        setColor(null);
        setCurrentIndex(0);
    }, [availableColors?.length]);

    return (
        <React.Fragment>
            <Container $isFormFieldSelection={isFormFieldSelection} $isForTableUse={isForTableUse}>
                {colors &&
                    colors.map((color, index) => (
                        // Key must NOT include `color`: changing it via the picker would remount the
                        // ColorBox, leaving useDropdown's elementRef pointing at a detached node →
                        // getBoundingClientRect() returns 0 → picker jumps to top-left. The new color
                        // still reaches ColorBoxFill via props, so the swatch updates without a remount.
                        <ColorContainer key={index}>
                            <ColorBox
                                onClick={(e) => {
                                    if (!onClickDisabled === true) {
                                        setCurrentIndex((prev) => {
                                            prev = index;
                                            return prev;
                                        });
                                        setCurrentColor((prev) => {
                                            prev = hexToHSL(color);
                                            return prev;
                                        });
                                        setColor(color);
                                        setOpen(e.currentTarget as HTMLElement, 'right', 'bottom');
                                    }
                                }}
                                $isFormFieldSelection={isFormFieldSelection}
                                $isForTableUse={isForTableUse}
                            >
                                <ColorBoxFill color={color} />

                                {!onClickDisabled && (
                                    <ColorBoxArrow $isFormFieldSelection={isFormFieldSelection} $isForTableUse={isForTableUse}>
                                        <Icon>
                                            <AngleDown />
                                        </Icon>
                                    </ColorBoxArrow>
                                )}

                                {onRemoveClick && (
                                    <ColorBoxClose
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onRemoveClick(index);
                                        }}
                                    >
                                        <Icon>
                                            <MdClose />
                                        </Icon>
                                    </ColorBoxClose>
                                )}
                            </ColorBox>
                            {showHexCode && (
                                <EditableHexCode
                                    color={color}
                                    isFormFieldSelection={isFormFieldSelection}
                                    onChange={(newColor) => onChange(colorIndex ?? index, newColor, '')}
                                />
                            )}
                        </ColorContainer>
                    ))}

                {onAddClick && (
                    <ColorBox onClick={onAddClick}>
                        <Icon>
                            <MdAdd />
                        </Icon>
                    </ColorBox>
                )}

                {color && isOpen && (
                    <Dropdown>
                        <ColorPicker
                            availableColors={availableColors}
                            color={availableColors ? null : color}
                            onChange={(color, name) => {
                                setCurrentColor(hexToHSL(color));
                                setColor(color);
                                onChange(colorIndex || currentIndex, color, name || '');
                            }}
                        />
                    </Dropdown>
                )}
            </Container>
        </React.Fragment>
    );
};

export default ColorSelection;
